<template>
  <div class="space-y-6">
    <form @submit.prevent="handleSubmit" class="space-y-4">
      <div class="relative">
        <input
          type="text"
          v-model="query"
          placeholder="Ask a question about your wool industry data..."
          class="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
        />
        <button
          type="submit"
          :disabled="loading || !query.trim()"
          class="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
        >
          <svg v-if="loading" class="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <svg v-else class="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <div class="flex flex-wrap gap-2">
        <span class="text-sm text-gray-600 self-center">Try:</span>
        <button
          v-for="(example, index) in exampleQueries"
          :key="index"
          type="button"
          @click="handleExampleQuery(example)"
          class="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
        >
          {{ example }}
        </button>
      </div>
    </form>

    <div v-if="result" class="space-y-4">
      <div class="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 class="font-semibold text-gray-900 mb-3 flex items-center">
          <svg class="w-5 h-5 mr-2 text-green-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M14 2V8H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Answer
        </h3>
        <div class="text-gray-700 whitespace-pre-wrap">
          <div v-for="(line, index) in answerLines" :key="index" v-html="renderTextWithBold(line)"></div>
        </div>
      </div>

      <div v-if="result.reasoning" class="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 shadow-md">
        <h3 class="font-semibold text-amber-900 mb-2 flex items-center">
          <svg class="w-5 h-5 mr-2 text-amber-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          My Thinking Process
        </h3>
        <p class="text-xs text-amber-700 mb-4 italic">
          Here's how I worked through your question
        </p>
        <div class="text-gray-800 leading-relaxed whitespace-pre-wrap">
          <div v-for="(line, index) in reasoningLines" :key="index" v-html="renderTextWithBold(line)"></div>
        </div>
      </div>

      <div v-if="result.entities && result.entities.length > 0" class="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 class="font-semibold text-gray-900 mb-3 flex items-center">
          <svg class="w-5 h-5 mr-2 text-green-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7709 21.0141C12.5281 21.1148 12.2678 21.1666 12.005 21.1666C11.7422 21.1666 11.4819 21.1148 11.2391 21.0141C10.9963 20.9135 10.7757 20.766 10.59 20.58L2 12V2H12L20.59 10.59C20.9625 10.9647 21.1716 11.4716 21.1716 12C21.1716 12.5284 20.9625 13.0353 20.59 13.41Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M7 7H7.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Related Entities ({{ result.entities.length }})
        </h3>
        <div class="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto">
          <div
            v-for="(entity, index) in result.entities.slice(0, 20)"
            :key="index"
            class="p-3 bg-gray-50 rounded border border-gray-200"
          >
            <div class="text-sm font-medium text-gray-900">
              {{ entity.value || entity.id }}
            </div>
            <div class="text-xs text-gray-500 mt-1">
              Type: {{ entity.type }}
            </div>
            <div v-if="entity.occurrences" class="text-xs text-gray-500">
              Occurrences: {{ entity.occurrences }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="result.sources && result.sources.length > 0" class="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 class="font-semibold text-gray-900 mb-3">
          Source Documents ({{ result.sources.length }})
        </h3>
        <div class="space-y-2 max-h-64 overflow-y-auto">
          <div
            v-for="(source, index) in result.sources.slice(0, 10)"
            :key="index"
            class="p-3 bg-gray-50 rounded border border-gray-200"
          >
            <div class="text-sm font-medium text-gray-900">
              {{ source.filename }}
            </div>
            <div v-if="source.relevance_score > 0" class="text-xs text-gray-500 mt-1">
              Relevance: {{ source.relevance_score }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QueryInterface',
  
  data() {
    return {
      query: '',
      loading: false,
      result: null,
      exampleQueries: [
        'Is consignment PFE-CNMEL-25-02 export ready?',
        'Why is consignment SCX-CNTJN-25-03 blocked?',
        'Where is bale IBG-01801?',
        'Show me information about bale IBG-01801',
        'What is the test result for WTL-593549?',
        'List all consignments',
      ]
    };
  },
  
  mounted() {
    // Check if there's a query from the Command Palette
    const savedQuery = sessionStorage.getItem('domain_graph_query');
    if (savedQuery) {
      this.query = savedQuery;
      sessionStorage.removeItem('domain_graph_query');
      // Execute the query automatically
      this.$nextTick(() => {
        this.handleQuery(savedQuery);
      });
    }
  },

  computed: {
    answerLines() {
      return this.result?.answer ? this.result.answer.split('\n') : [];
    },
    reasoningLines() {
      return this.result?.reasoning ? this.result.reasoning.split('\n') : [];
    }
  },
  
  methods: {
    renderTextWithBold(text) {
      if (!text) return '';
      return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    },
    
    async handleQuery(queryText) {
      if (!queryText.trim()) return;
      
      this.loading = true;
      this.result = null;
      
      try {
        const response = await this.$axios.post('/api/domain-graph/query', { query: queryText });
        this.result = response.data;
      } catch (error) {
        this.result = {
          answer: 'Error processing query: ' + error.message,
          sources: [],
          entities: [],
        };
      } finally {
        this.loading = false;
      }
    },
    
    handleSubmit() {
      this.handleQuery(this.query);
    },
    
    handleExampleQuery(example) {
      this.query = example;
      this.handleQuery(example);
    }
  }
};
</script>

<style scoped>
.space-y-6 > * + * {
  margin-top: 1.5rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.space-y-2 > * + * {
  margin-top: 0.5rem;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 0.75rem;
}
</style>
